import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  generatePath,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom';
import { useErrorHandler } from 'react-error-boundary';
import { upperCase } from 'lodash';
import { format as formatDate } from 'date-fns';
import { Translations } from '@/lib/translation';

import {
  Button,
  CheckCircleIcon,
  ErrorIcon,
  ExpandMoreIcon,
  Header as PageHeader,
  Modal,
  NullIcon,
  RemoveRedEyeOutlinedIcon,
  SaveAltIcon,
  Spinner,
  Text,
  CopyIcon,
} from 'elmo-elements';
import {
  HeaderButtonProp,
  HeaderTabLinkProps,
  PrimaryButtonType,
} from 'elmo-elements/dist/Header/type';
import {
  canPreviewLive,
  convertStringToTitleCase,
  generateLiveUrl,
  generateReviewUrl,
  isElearningPublishedToBreathe,
  isElearningPublishedToLive,
  isElearningPublishedToLiveNew,
  isElearningPublishedToReview,
  isElearningPublishedToReviewNew,
  openPlayerUIWindow,
} from '@/lib/util';
import config from '@/lib/config/index';
import {
  useGetCurrentTenantProfileQuery,
  useGetElearningDetailsQuery,
  useGetElearningQuery,
  useGetPublishResultQuery,
  usePublishElearningMutation,
  useDownloadScormPackageMutation,
} from '@/modules/services';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { ElearningStatus, SyncStatus } from '@/modules/types/elearning';
import ModuleRoutes, { ElearningParamType } from '@/pages/Module/routes';

import ElearningReviewersRoutes from '@/pages/Module/Reviewers/routes';
import QuizRoutes from '@/pages/Quiz/routes';
import {
  DATETIME_FORMAT,
  Namespace,
  NAMESPACE_DOMAIN_MAPPING,
} from '@/app/const';

import './Header.scss';
import { appData } from '@/lib/data';
import {
  ElearningPublishAction,
  ElearningPublishActionType,
  ElearningPublishChannel,
  ElearningPublishTarget,
  ElearningPublishTargetType,
} from '@/modules/types/elearning/publish.enum';
import SubdomainSelectList from './SubdomainSelectList';
import MultiRegionSelectList from './MultiRegionSelectList';
import { CustomHttpError } from '@/app/types';
import ChannelSelectList from './PublishSelectList';
import { useFlags } from 'launchdarkly-react-client-sdk';

// eslint-disable-next-line no-shadow
enum PageHeaderTabs {
  module = 'MODULE',
  quiz = 'QUIZ',
  reviewers = 'REVIEWERS',
}

const PUBLISH_CHANNEL = {
  [ElearningPublishChannel.Generic]: 'ELMO Learning',
  [ElearningPublishChannel.SCORM]: 'SCORM Package',
  [ElearningPublishChannel.CourseLibrary]: 'Course Library',
  [ElearningPublishChannel.VideoLibrary]: 'Video Library',
  [ElearningPublishChannel.BREATHE]: 'Breathe',
};

const determineTab = (url: string) => {
  let currentActiveTab = PageHeaderTabs.module;
  const urlTab = upperCase(url.split('/').pop());
  switch (true) {
    case urlTab === PageHeaderTabs.reviewers:
      currentActiveTab = PageHeaderTabs.reviewers;
      break;
    case url.includes(PageHeaderTabs.quiz.toLowerCase()):
      currentActiveTab = PageHeaderTabs.quiz;
      break;

    default:
      currentActiveTab = PageHeaderTabs.module;
      break;
  }

  return currentActiveTab;
};

const Header = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(determineTab(location.pathname));
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [publishType, setPublishType] = useState<ElearningPublishTargetType>(
    ElearningPublishTarget.REVIEW,
  );
  const [publishAction, setPublishAction] =
    useState<ElearningPublishActionType>(ElearningPublishAction.CREATE);
  const [channel, setChannel] = useState<ElearningPublishChannel | null>(null);
  const {
    learningElaCourseLibraryAdmin: isElaCourseLibraryEnabled,
    learningElaScormExportEnable: isScormExportEnabled,
    learningElaBreatheIntegration: isBreatheFlag,
    learningElaCourseLibraryAdminMultiRegion: isMultiRegionEnabled,
  } = useFlags();
  const { elearningId } = useParams<ElearningParamType>();
  const handleError = useErrorHandler();
  const elearning = useGetElearningQuery(elearningId);
  const elearningDetails = useGetElearningDetailsQuery(elearningId);
  const [publishElearning, publishElearningStatus] =
    usePublishElearningMutation();
  const [downloadScormPackage, downloadScormStatus] =
    useDownloadScormPackageMutation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [skip, setSkip] = useState(true);
  const [publishInProgress, setPublishInProgress] = useState(false);
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const { data: tenantData } = useGetCurrentTenantProfileQuery();

  const isScormEnabled = isScormExportEnabled && tenantData?.scorm;
  const isBreatheEnabled =
    isBreatheFlag && tenantData?.configMetadata?.enableBreathe;
  const isChannelsEnabled =
    isElaCourseLibraryEnabled || isScormEnabled || isBreatheEnabled;

  const defaultSubdomain = `${tenantData?.tenant.identifier}.${
    NAMESPACE_DOMAIN_MAPPING[tenantData?.tenant.namespace as Namespace]
  }`;

  const subdomains = tenantData
    ? (tenantData.tenant.subdomain ?? '').trim().split('\n').filter(Boolean)
    : [];
  const publishResult = useGetPublishResultQuery(
    { elearningId, type: publishType },
    {
      pollingInterval: 1000,
      skip,
    },
  );
  const [subdomainSelection, setSubdomainSelection] = useState<Set<string>>(
    new Set([defaultSubdomain]),
  );
  const [targetRegions, setTargetRegions] = useState<string[]>([]);

  const updateActiveTab = (url: string) => {
    setActiveTab(determineTab(url));
  };

  useEffect(() => {
    updateActiveTab(location.pathname);
  });

  useEffect(() => {
    if (publishElearningStatus.isError) {
      // close the modal in case of error
      setIsConfirmModalOpen(false);
      const error = publishElearningStatus.error as CustomHttpError;

      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: error.data.message,
        },
      });
      setPublishInProgress(false);
    }
    if (
      !publishElearningStatus.isLoading &&
      publishElearningStatus.isSuccess &&
      publishElearningStatus.data
    ) {
      publishElearningStatus.reset();
      if (
        publishElearningStatus.data.statusCode === 200 ||
        publishElearningStatus.data.statusCode === 201
      ) {
        setSkip(false);
      }
    }
  }, [publishElearningStatus]);

  useEffect(() => {
    if (publishResult.isError) {
      setSkip(true);
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message:
            'An error occurred and we were unable to publish this module. Please try again.',
        },
      });
    }

    const getPublishTargetLabel = () => {
      if (publishType === ElearningPublishTarget.MULTI_REGION) {
        return 'Other Regions';
      }

      if (publishType === ElearningPublishTarget.LIVE && channel !== null) {
        return PUBLISH_CHANNEL[channel];
      }

      return convertStringToTitleCase(publishType);
    };

    // Using isFetching because of caching issue for succeeding publish request w/o refreshing page.
    if (
      !publishResult.isFetching &&
      publishResult.isSuccess &&
      !publishResult.data
    ) {
      const publishTarget = getPublishTargetLabel();

      setSkip(true);
      elearningDetails.refetch();
      setIsConfirmModalOpen(false);
      setPublishInProgress(false);
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message:
            Translations.elearning.publish.successMessage.default.replace(
              '::PUBLISH_TARGET::',
              publishTarget,
            ),
        },
      });
      elearning.refetch();
    }
  }, [publishResult]);

  if (elearning.isLoading) {
    return <div className="loading" />;
  }

  if (elearning.isError) {
    handleError(elearning.error);
  }

  const handleDraft = () => {
    if (elearning.data) {
      openPlayerUIWindow(
        elearning.data,
        `/${ElearningPublishTarget.DRAFT}/${elearning.data?.id}`,
        ElearningPublishTarget.DRAFT,
        appData.playerTheme.default.windowMetadata,
      );
    }
  };

  const handleReview = () => {
    const elearningPublishStatus = elearning.data?.statusMetadata;

    if (
      elearning.data &&
      elearningPublishStatus?.status &&
      isElearningPublishedToReview(elearningPublishStatus.status)
    ) {
      const windowUrl = generateReviewUrl(
        elearning.data,
        config.OldCBURL,
        config.OldCBTOKEN,
      );

      openPlayerUIWindow(
        elearning.data,
        windowUrl,
        ElearningPublishTarget.REVIEW,
        appData.playerTheme.default.windowMetadata,
      );
    }
  };

  const handleLive = () => {
    const elearningPublishStatus = elearning.data?.statusMetadata;
    if (
      elearningPublishStatus?.status &&
      elearning.data &&
      isElearningPublishedToReview(elearningPublishStatus.status) &&
      isElearningPublishedToLive(elearningPublishStatus.lmsStatus)
    ) {
      // If the elearning has not been published to live in new CB, load elearning from old CB
      const windowUrl = generateLiveUrl(
        elearning.data,
        config.OldCBURL,
        config.OldCBTOKEN,
      );

      openPlayerUIWindow(
        elearning.data,
        windowUrl,
        ElearningPublishTarget.LIVE,
        appData.playerTheme.default.windowMetadata,
      );
    }
  };

  const handleCopyBreatheUrl = () => {
    const tenantId = elearning.data?.tenantId;
    if (!tenantId || !elearningId) return;
    const url = `${window.location.origin}/breathe/${tenantId}/${elearningId}/launch`;
    navigator.clipboard.writeText(url).then(() => {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Breathe URL copied to clipboard',
        },
      });
    });
  };

  const handleScormDownload = async () => {
    setDownloadInProgress(true);
    try {
      const blob = await downloadScormPackage({ elearningId }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${elearning.data?.name}_${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      downloadScormStatus.reset();
    } catch (error: any) {
      // Check if the response is actually an error (JSON) instead of a zip file
      let errorMessage = 'Download failed. Please try again.';
      if (
        error.data &&
        error.data instanceof Blob &&
        error.data.type === 'application/json'
      ) {
        const errorText = await error.data?.text();
        const errorData = JSON.parse(errorText);
        const errorPart = errorData?.error ?? 'Unknown error';
        const messagePart = errorData?.message ? ` ,${errorData.message}` : '';
        errorMessage = `${errorPart}${messagePart}`;
      }
      // Handle different error types
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: errorMessage,
        },
      });
    } finally {
      setDownloadInProgress(false);
    }
  };

  const getHeaderButtons = (): HeaderButtonProp[] => {
    const previewButtons: HeaderButtonProp[] = [];
    previewButtons.push({
      icon: '',
      label: 'Preview Draft',
      id: 'ButtonDraft',
      onClick: handleDraft,
      showBtnLabel: true,
      isDisabled:
        elearningDetails.data &&
        +elearningDetails.data.details.totalFrames === 0,
    });

    previewButtons.push({
      icon: '',
      label: 'Preview Review',
      id: 'ButtonReview',
      onClick: handleReview,
      showBtnLabel: true,
      isDisabled:
        elearningDetails.data &&
        elearningDetails.data.statusMetadata &&
        +elearningDetails.data.statusMetadata.status <= ElearningStatus.Draft,
    });

    previewButtons.push({
      icon: '',
      label: 'Preview Live',
      id: 'ButtonLive',
      onClick: handleLive,
      showBtnLabel: true,
      isDisabled:
        elearningDetails.data &&
        !canPreviewLive(elearningDetails.data.statusMetadata),
    });

    const buttons: HeaderButtonProp[] = [
      {
        icon: <RemoveRedEyeOutlinedIcon />,
        label: '',
        onClick: () => {},
        children: previewButtons,
      },
    ];

    // Check if SCORM download should be shown
    const canShowScormDownload =
      isScormEnabled && elearningDetails.data?.statusMetadata?.publishedToScorm;

    if (canShowScormDownload) {
      buttons.unshift({
        icon: downloadInProgress ? <Spinner size="s" /> : <SaveAltIcon />,
        label: 'download',
        onClick: handleScormDownload,
        isDisabled: downloadInProgress,
      });
    }

    const canShowCopyBreatheUrl =
      isBreatheEnabled &&
      elearning.data?.tenantId &&
      elearningId &&
      elearningDetails.data?.statusMetadata &&
      isElearningPublishedToBreathe(elearningDetails.data.statusMetadata);
    if (canShowCopyBreatheUrl) {
      buttons.unshift({
        icon: <CopyIcon />,
        label: 'Copy Breathe URL',
        onClick: handleCopyBreatheUrl,
      });
    }
    return buttons;
  };

  const getPublishToReviewInfo = () => {
    if (!elearningDetails.data) {
      return <NullIcon />;
    }

    const isReviewPublished = isElearningPublishedToReview(
      +elearningDetails.data.statusMetadata.status,
    );
    const isReviewUnsynced =
      +elearningDetails.data.statusMetadata.courseSync === SyncStatus.unsynced;

    if (isReviewPublished && isReviewUnsynced) {
      return (
        <div>
          <Text color="warning">
            <ErrorIcon />
          </Text>
          {elearningDetails.data.date.publishedReview && (
            <span className="publish-info">
              Last published{' '}
              {formatDate(
                new Date(elearningDetails.data.date.publishedReview),
                DATETIME_FORMAT,
              )}
            </span>
          )}
        </div>
      );
    }
    return (
      <div>
        <Text color="success">
          <CheckCircleIcon />
        </Text>
        {elearningDetails.data.date.publishedReview && (
          <span className="publish-info">
            Last published{' '}
            {formatDate(
              new Date(elearningDetails.data.date.publishedReview),
              DATETIME_FORMAT,
            )}
          </span>
        )}
      </div>
    );
  };

  const getPublishToLiveInfo = () => {
    if (!elearningDetails.data) {
      return <NullIcon />;
    }

    const isLivePublished =
      isElearningPublishedToLive(
        +elearningDetails.data.statusMetadata.lmsStatus,
      ) && isElearningPublishedToLiveNew(elearningDetails.data.statusMetadata);
    const isLiveUnsynced =
      +elearningDetails.data.statusMetadata.lmsSync === SyncStatus.unsynced;

    if (!isLivePublished)
      return (
        <div>
          <CheckCircleIcon />

          <span className="publish-info">Not Published</span>
        </div>
      );

    if (!isLiveUnsynced)
      return (
        <div>
          <Text color="success">
            <CheckCircleIcon />
          </Text>
          {elearningDetails.data.date.publishedLive && (
            <span className="publish-info">
              Last published{' '}
              {formatDate(
                new Date(elearningDetails.data.date.publishedLive),
                DATETIME_FORMAT,
              )}
            </span>
          )}
        </div>
      );

    return (
      <div>
        <Text color="warning">
          <ErrorIcon />
        </Text>
        {elearningDetails.data.date.publishedLive && (
          <span className="publish-info">
            Last published{' '}
            {formatDate(
              new Date(elearningDetails.data.date.publishedLive),
              DATETIME_FORMAT,
            )}
          </span>
        )}
      </div>
    );
  };

  const getPrimaryButton = (): PrimaryButtonType => {
    if (
      elearningDetails.data &&
      +elearningDetails.data.details.totalFrames === 0
    ) {
      return {
        icon: '',
        label: 'Publish',
        id: 'ButtonPublish',
        onClick: () => {},
        isDisabled: true,
      };
    }

    if (
      elearningDetails.data &&
      elearningDetails.data.statusMetadata &&
      +elearningDetails.data.statusMetadata.status <= ElearningStatus.Draft
    )
      return {
        icon: '',
        label: 'Publish to Review',
        id: 'ButtonPublish',
        onClick: () => {
          setPublishType(ElearningPublishTarget.REVIEW);
          setPublishAction(ElearningPublishAction.CREATE);
          setIsConfirmModalOpen(true);
        },
      };

    if (
      elearningDetails.data &&
      elearningDetails.data.statusMetadata &&
      isElearningPublishedToReview(elearningDetails.data.statusMetadata.status)
    )
      return {
        icon: <ExpandMoreIcon />,
        label: 'Publish',
        id: 'ButtonPublish',
        onClick: () => {},
        children: [
          {
            icon: getPublishToReviewInfo(),
            label: 'Update Review',
            id: 'ButtonPublishReview',
            onClick: () => {
              setPublishType(ElearningPublishTarget.REVIEW);
              setPublishAction(ElearningPublishAction.UPDATE);
              setIsConfirmModalOpen(true);
            },
            isDisabled:
              elearningDetails.data.statusMetadata &&
              +elearningDetails.data.statusMetadata.status <=
                ElearningStatus.Draft,
          },
          {
            icon: getPublishToLiveInfo(),
            label: `${
              isElearningPublishedToLive(
                +elearningDetails.data.statusMetadata.lmsStatus,
              ) &&
              isElearningPublishedToLiveNew(
                elearningDetails.data.statusMetadata,
              )
                ? 'Update'
                : 'Publish to'
            } Live`,
            id: 'ButtonPublishLive',
            onClick: () => {
              setPublishType(ElearningPublishTarget.LIVE);
              setIsConfirmModalOpen(true);

              if (isChannelsEnabled) {
                setChannel(null);
              } else {
                // Add default channel if neither CourseLibrary nor SCORM is enabled
                setChannel(ElearningPublishChannel.Generic);
              }
            },
            isDisabled: !isElearningPublishedToReviewNew(
              elearningDetails.data.statusMetadata,
            ),
          },
          ...(isElaCourseLibraryEnabled && isMultiRegionEnabled
            ? [
                {
                  icon: <NullIcon />,
                  label: 'Publish to Other Regions',
                  id: 'ButtonPublishMultiRegion',
                  onClick: () => {
                    setPublishType(ElearningPublishTarget.MULTI_REGION);
                    setTargetRegions([]);
                    setIsConfirmModalOpen(true);
                  },
                },
              ]
            : []),
        ],
      };

    return {
      icon: '',
      label: 'Default',
      id: '',
      onClick: () => {},
    };
  };

  const handleModule = () => {
    if (activeTab === PageHeaderTabs.module) {
      return;
    }
    setActiveTab(PageHeaderTabs.module);
    history.push(
      generatePath(ModuleRoutes.path, {
        elearningId: elearning.data?.id,
      }),
    );
  };

  const handleQuiz = () => {
    if (activeTab === PageHeaderTabs.quiz) {
      return;
    }
    history.push(
      generatePath(QuizRoutes.path, {
        elearningId: elearning.data?.id,
      }),
    );
    setActiveTab(PageHeaderTabs.quiz);
  };
  const handleReviewers = () => {
    if (activeTab === PageHeaderTabs.reviewers) {
      return;
    }
    history.push(
      generatePath(ElearningReviewersRoutes.path, {
        elearningId: elearning.data?.id,
      }),
    );
    setActiveTab(PageHeaderTabs.reviewers);
  };

  // Only show reviewer tab if already published to NCB
  const ReviewerTab =
    elearning.data?.statusMetadata.publishedToReview &&
    elearning.data?.statusMetadata.publishedToReview !== 0
      ? [
          {
            label: 'Reviewers',
            active: PageHeaderTabs.reviewers === activeTab,
            onClick: () => handleReviewers(),
          },
        ]
      : [];

  const getHeaderTabs = (): HeaderTabLinkProps[] =>
    [
      {
        label: 'Module',
        active: PageHeaderTabs.module === activeTab,
        onClick: () => handleModule(),
      },
      {
        label: 'Quiz',
        active: PageHeaderTabs.quiz === activeTab,
        onClick: () => handleQuiz(),
      },
    ].concat(ReviewerTab);

  const handleCancelPublish = () => {
    setSubdomainSelection(new Set([defaultSubdomain]));
    setTargetRegions([]);
    setIsConfirmModalOpen(false);
  };

  const getSubdomains = (isMultiRegion: boolean) => {
    if (isMultiRegion) return defaultSubdomain;

    const isElearningChannel =
      channel !== null && channel === ElearningPublishChannel.Generic;

    return subdomainSelection.size > 0 && isElearningChannel
      ? Array.from(subdomainSelection).join(',')
      : defaultSubdomain;
  };

  const handleConfirmPublish = (type: ElearningPublishTargetType) => {
    const isMultiRegion = type === ElearningPublishTarget.MULTI_REGION;
    const publishSubdomains = getSubdomains(isMultiRegion);
    const publishPayload = {
      elearningId,
      type,
      entity: 'all',
      subdomains: publishSubdomains,
      channel:
        channel ??
        (isMultiRegion
          ? ElearningPublishChannel.CourseLibrary
          : ElearningPublishChannel.Generic),
      ...(isMultiRegion && { targetRegions }),
    };

    setPublishInProgress(true);
    publishElearning(publishPayload);
  };

  const getPublishType = (publishTargetType: ElearningPublishTargetType) =>
    convertStringToTitleCase(publishTargetType);

  const showPublishSelectList = () => {
    const isSubdomainSelectedWithChannel =
      isChannelsEnabled &&
      subdomains.length > 1 &&
      channel === ElearningPublishChannel.Generic;
    const isSubdomainSelected = !isChannelsEnabled && subdomains.length > 1;

    if (isSubdomainSelectedWithChannel || isSubdomainSelected) {
      return (
        <SubdomainSelectList
          className="subdomainContainer"
          elearningId={elearningId}
          defaultSubdomain={defaultSubdomain}
          onChange={(selection) => {
            /* Rejoin subdomains since BE expects a single string */
            setSubdomainSelection(new Set([defaultSubdomain, ...selection]));
          }}
        />
      );
    }

    if (isChannelsEnabled) {
      return (
        <ChannelSelectList
          onChange={(value) => {
            setChannel(value);
          }}
        />
      );
    }

    return <div />;
  };

  const isMultiRegion = publishType === ElearningPublishTarget.MULTI_REGION;

  const getModalTitle = () => {
    if (publishInProgress) {
      return Translations.elearning.publish.inProgressMessage.default;
    }

    if (isMultiRegion) {
      return Translations.elearning.publish.multiRegionTitle.default;
    }

    return Translations.elearning.publish.actionMessage.default.replace(
      '::PUBLISH_TARGET::',
      getPublishType(publishType),
    );
  };

  const isContinueDisabled =
    (isChannelsEnabled &&
      channel === null &&
      publishType !== ElearningPublishTarget.REVIEW &&
      !isMultiRegion) ||
    (isMultiRegion && targetRegions.length === 0);

  return (
    <>
      <Modal
        id="publishElearning"
        isOpened={isConfirmModalOpen}
        title={getModalTitle()}
        closeModal={handleCancelPublish}
        primaryButton={
          !publishInProgress && (
            <Button
              onClick={() => handleConfirmPublish(publishType)}
              isLoading={publishElearningStatus.isLoading}
              type="primary"
              id="publish-elearning-review-confirm"
              isUppercase={false}
              isDisabled={isContinueDisabled}
            >
              Continue
            </Button>
          )
        }
      >
        {publishInProgress ? (
          <div className="publish-in-progress-container">
            <div className="publish-in-progress-container__spinner">
              <Spinner size="xl" />
            </div>
            <p> {Translations.elearning.publish.inProgressInfo.default}</p>
          </div>
        ) : (
          <p>
            {publishAction === ElearningPublishAction.CREATE &&
              publishType === ElearningPublishTarget.REVIEW &&
              Translations.elearning.publish.actionMessageCreateReview.default}

            {publishAction === ElearningPublishAction.UPDATE &&
              publishType === ElearningPublishTarget.REVIEW &&
              Translations.elearning.publish.actionMessageUpdateReview.default}

            {publishType === ElearningPublishTarget.LIVE &&
              (subdomains.length > 1
                ? Translations.elearning.publish
                    .actionMessageCreateMultitenantLive.default
                : Translations.elearning.publish.actionMessageCreateLive
                    .default)}

            {isMultiRegion &&
              Translations.elearning.publish.actionMessageMultiRegion.default}
          </p>
        )}

        {publishType === ElearningPublishTarget.LIVE && !publishInProgress && (
          <>{showPublishSelectList()}</>
        )}

        {isMultiRegion && !publishInProgress && (
          <MultiRegionSelectList
            currentNamespace={tenantData?.tenant.namespace ?? ''}
            onChange={(selected) => setTargetRegions(selected)}
          />
        )}
      </Modal>

      <PageHeader
        title={
          <>
            {elearning.data?.name}
            <span className="elmo-header__last-saved">
              {!elearningDetails.data ? (
                <Spinner size="s" />
              ) : (
                `Last modified date ${formatDate(
                  new Date(elearningDetails.data?.lastModifiedDate),
                  DATETIME_FORMAT,
                )}`
              )}
            </span>
          </>
        }
        backButton={{
          title: '',
          onClick: history.goBack,
          ariaLabel: 'Back',
          id: 'header-back',
        }}
        buttons={getHeaderButtons()}
        primaryButton={getPrimaryButton()}
        tabs={getHeaderTabs()}
      />
    </>
  );
};

export default Header;
